// AG Grid specific utilities - column definitions, renderers, and grid configuration

import { ColumnState, GridApi } from '@ag-grid-community/core';
import { Ref } from 'vue';
import { useSessionStorage } from '@agyletime/vue-3-common/src/composables/sessionStorage';
import { Toast } from '@agyletime/vue-3-common/src/components/Toast';
import { DateColumn, ShiftMetadata } from './types';
import { createDateCellRenderer } from './cellRenderers';

const sessionStorage = useSessionStorage();

export const addPageSizeSelector = (
    gridApi: GridApi,
    pageSizeOption: Ref<number>,
    pageSizeOptions: number[],
    tableClass: string
): void => {
    if (!gridApi) return;
    const gridElement = document.querySelector(`.${tableClass} .ag-theme-playvox`);
    if (!gridElement) return;

    const paginationPanel = gridElement.querySelector('.ag-paging-panel');
    if (!paginationPanel) return;

    const container = document.createElement('div');
    container.className = 'page-size-container';

    const label = document.createElement('label');
    label.textContent = 'Page size:';
    label.className = 'page-size-label';

    const pageSizeSelector = document.createElement('select');
    pageSizeSelector.className = 'page-size-selector';

    pageSizeOptions.forEach((size) => {
        const option = document.createElement('option');
        option.value = size.toString();
        option.textContent = `${size}`;
        if (size === pageSizeOption.value) {
            option.selected = true;
        }
        pageSizeSelector.appendChild(option);
    });

    pageSizeSelector.addEventListener('change', (event) => {
        const newSize = Number((event.target as HTMLSelectElement).value);
        pageSizeOption.value = newSize;
        (gridApi as unknown as { paginationSetPageSize: (size: number) => void })?.paginationSetPageSize?.(newSize);
    });

    container.appendChild(label);
    container.appendChild(pageSizeSelector);
    paginationPanel.prepend(container);
};

export const saveCurrentGridSettings = (columnApi: unknown, filterBy: string) => {
    if (!columnApi) return;
    const columnState = (columnApi as { getColumnState: () => ColumnState[] }).getColumnState();
    if (columnState) {
        sessionStorage.set(`workstreams-and-interactions-${filterBy}-columnState`, { columnState });
        Toast.addToast('saveCurrentGridSettings', 'Successfully saved grid settings', 'SUCCESS');
    }
};

export const restoreCurrentGridSettings = (columnApi: unknown, columnState: Array<ColumnState>) => {
    if (columnState && columnApi) {
        (columnApi as { applyColumnState: (params: { state: ColumnState[]; applyOrder: boolean }) => void })
            .applyColumnState({
                state: columnState,
                applyOrder: true
            });
    }
};

export const updateNextButtonState = (
    gridApi: GridApi,
    tokens: Ref<string[]>,
    tableClass: string
) => {
    const currentPage = gridApi?.paginationGetCurrentPage() ?? 0;
    const nextToken = tokens.value[currentPage + 1];
  
    const nextButton = document.querySelector(`.${tableClass} .ag-paging-button[ref="btNext"]`) as HTMLElement;
    if (nextButton) {
        if (!nextToken) {
            nextButton.classList.add('disabled-pagination-button');
        } else {
            nextButton.classList.remove('disabled-pagination-button');
        }
    }
};

/**
 * Generate AG Grid column definitions for user-date grid
 * Matches Angular's dynamic date column creation
 */
export function generateUserDateColumnDefs(
    dateColumns: DateColumn[],
    columnDisplay: { [key: string]: boolean },
    groupBy: string
): unknown[] {
    const columnDefs = [
        {
            headerName: 'Team',
            field: 'teamId',
            width: 180,
            sortable: true,
            enableValue: true,
            filter: 'agTextColumnFilter',
            resizable: true,
            floatingFilter: true,
            cellClass: 'team-name-cell',
            rowGroup: groupBy === 'TEAMS',
            hide: true,
            wrapText: true,
            autoHeight: true,
            valueGetter: (params: { data?: Record<string, unknown> }) => {
                return params.data?.teamName || '';   // replace with your lookup map
            }
        },
        {
            headerName: 'Users',
            field: 'userDisplayName',
            pinned: 'left',
            width: 200,
            sortable: true,
            enableValue: true,
            filter: 'agTextColumnFilter',
            resizable: true,
            floatingFilter: true,
            cellClass: 'user-name-cell',
            wrapText: true,
            autoHeight: true,
            hide: groupBy === 'TEAMS'
        },
        {
            headerName: 'Email',
            field: 'userEmail',
            pinned: 'left',
            width: 200,
            sortable: true,
            enableValue: true,
            filter: 'agTextColumnFilter',
            resizable: true,
            floatingFilter: true,
            cellClass: 'user-name-cell',
            wrapText: true,
            autoHeight: true,
            hide: !columnDisplay.email
        },
        {
            headerName: 'Employee ID',
            field: 'employeeId',
            pinned: 'left',
            width: 200,
            sortable: true,
            enableValue: true,
            filter: 'agTextColumnFilter',
            resizable: true,
            floatingFilter: true,
            cellClass: 'user-name-cell',
            wrapText: true,
            autoHeight: true,
            hide: !columnDisplay.userId
        },
        // Dynamic date columns
        ...dateColumns.map((dateCol) => {
            return {
                headerName: dateCol.headerName,
                field: dateCol.field,
                width: 260,
                sortable: true,
                enableValue: true,
                filter: 'agTextColumnFilter',
                resizable: true,
                floatingFilter: true,
                cellClass: dateCol.isWeekend ? 'weekend-cell' : 'weekday-cell',
                wrapText: true, // Enable text wrapping for multi-line content
                autoHeight: true, // Enable auto height for cells with multiple shifts
                
                // Custom cell renderer: Show colored dot + time text
                cellRenderer: createDateCellRenderer(dateCol),
                
                // Add valueGetter for filtering/sorting
                valueGetter: (params: { data?: Record<string, unknown> }) => {
                    const value = params.data?.[dateCol.field];
                    if (!value || typeof value !== 'object') return '';
                    return (value as ShiftMetadata).displayText || '';
                }
            };
        })
    ];
    
    return columnDefs;
}
